import { findeDokument, normalisiere } from '../../../services/mitgeltendeDokumente';
import type { SteckbriefUnterlage } from '../../../services/projektSteckbrief';
import type { Unterlage } from '../types';
import {
    abgleicheAblage, verknuepfbareDokumente,
    type AblageDokument, type UnterlagenEntwurf,
} from './anlagen';
import { herausgeberVon, katalogDokumentZu, naechsteSortierung } from './katalogVorschlaege';

/**
 * Kapitel 9 aus dem Unterlagenstatus der Planungsphase.
 *
 * ═══ WOZU ═════════════════════════════════════════════════════════════════
 * Die Unterlagen-Checkliste der Planungsphase fuehrt je Dokument einen Status
 * und — bei `liegt_vor` — den Beleg in der Projektablage (`dokument_id`). Der
 * Steckbrief reicht die A+S-relevanten Zeilen weiter (`asplan` = die
 * vorgeschlagene Art in Kapitel 9). Diese Datei sagt, welche davon im Plan
 * noch fehlen, und macht daraus die Zeile fuer `asplan_unterlagen` — MIT dem
 * Beleg, denn genau der unterscheidet diesen Vorschlag vom Katalogvorschlag
 * (`katalogVorschlaege.ts`): dort weiss der Katalog, DASS ein Gutachten
 * gebraucht wird; hier weiss die Planungsphase, DASS es vorliegt, und zeigt
 * auf das Dokument.
 *
 * ═══ „LIEGT VOR" OHNE BELEG IST KEIN NACHWEIS ═════════════════════════════
 * Kapitel 9 druckt „liegt vor" allein an `dokument_id` (`anlagen.ts`). Ein
 * Status `liegt_vor` ohne verknuepftes Dokument wird deshalb NICHT als
 * belegte Zeile angeboten — er erschiene im Plan als unbelegte Behauptung.
 * Er steht als `ohne_beleg` in der Liste, damit sichtbar ist, was der
 * Planungsphase fehlt; aufnehmen laesst sich das Dokument weiterhin ueber den
 * Katalogvorschlag (rot, bis jemand verknuepft).
 *
 * ═══ DER ABGLEICH ENTSCHEIDET, NICHT DIESE DATEI ══════════════════════════
 * Ob ein Dokument einsehbar ist und ob schon eine Unterlage darauf zeigt,
 * sagt `abgleicheAblage` (`unverknuepft`, `verwaist`) zusammen mit
 * `verknuepfbareDokumente`. Hier wird nur gelesen, was dort herauskommt —
 * eine zweite Regel fuer „admin-gesperrt" oder „verwaist" waere die sichere
 * Quelle fuer zwei verschiedene Antworten auf dieselbe Frage.
 *
 *   neu              — Beleg einsehbar, keine Unterlage zeigt darauf, keine
 *                      Zeile gleichen Namens: eine neue Zeile mit Beleg.
 *   verknuepfen      — es gibt schon eine Zeile dieses Dokuments OHNE Beleg
 *                      (etwa aus dem Katalogvorschlag): sie wird belegt, keine
 *                      zweite Zeile.
 *   uebernommen      — eine Unterlage zeigt bereits auf den Beleg.
 *   nicht_einsehbar  — die Ablage liefert das Dokument nicht (geloescht oder
 *                      admin-gesperrt). Kein Knopf: ein Verweis, den der Leser
 *                      nicht pruefen kann, ist kein Nachweis.
 *   ohne_beleg       — siehe oben.
 *
 * ═══ VORSCHLAG, NICHT VORBELEGUNG ═════════════════════════════════════════
 * Uebernommen wird nur auf Knopfdruck, eine Zeile je Klick. Bezeichnung ist
 * der Name der Checkliste (bei Katalogdokumenten der Katalogname — so erkennt
 * `katalogDokumentZu` die Zeile wieder und der Katalog schlaegt sie nicht
 * noch einmal vor), Herausgeber die Lieferantenrolle wie beim
 * Katalogvorschlag, Nummer und Kennung bleiben leer (Begruendung dort).
 *
 * Diese Datei kennt weder DOM noch Supabase noch React.
 */

export type SteckbriefAnlageStand = 'neu' | 'verknuepfen' | 'uebernommen' | 'nicht_einsehbar' | 'ohne_beleg';

export interface SteckbriefAnlage {
    /** Id der Checklistenzeile (Katalog-Id, `plan:…` oder `eigen:…`). */
    id: string;
    name: string;
    /** Vorgeschlagene Art in Kapitel 9 (= `SteckbriefUnterlage.asplan`). */
    art: Unterlage['art'];
    /** Der Beleg in der Projektablage; `null` bei `ohne_beleg`. */
    dokumentId: string | null;
    /** Titel des Belegs, sofern einsehbar. */
    dokumentTitel: string | null;
    stand: SteckbriefAnlageStand;
    /** Bei `verknuepfen`: die vorhandene Zeile, die den Beleg bekommt. */
    ziel: Pick<Unterlage, 'id' | 'bezeichnung'> | null;
}

/** Grundlagen vor Anlagen — dieselbe Reihenfolge wie im gedruckten Kapitel 9. */
const ART_REIHENFOLGE: Record<Unterlage['art'], number> = { grundlage: 0, anlage: 1 };

/**
 * Ob eine vorhandene Unterlage dasselbe Dokument meint wie die Checklisten-
 * zeile: bei Katalogdokumenten ueber die Wiedererkennung des Katalogs
 * (Name, Alias, Wortgrenze), sonst ueber den normalisierten Namen.
 */
const meintDasselbe = (u: Unterlage, sb: SteckbriefUnterlage): boolean => {
    if (!u || !u.bezeichnung) return false;
    if (sb.quelle === 'katalog' && katalogDokumentZu(u.bezeichnung)?.id === sb.id) return true;
    const a = normalisiere(u.bezeichnung);
    return Boolean(a) && a === normalisiere(sb.name);
};

/**
 * Die A+S-relevanten Unterlagen der Planungsphase mit Status `liegt_vor`,
 * jede mit dem Stand, den sie in Kapitel 9 hat.
 */
export const steckbriefAnlagen = (
    steckbrief: readonly SteckbriefUnterlage[],
    unterlagen: readonly Unterlage[],
    dokumente: readonly AblageDokument[],
): SteckbriefAnlage[] => {
    const liste = (unterlagen || []).filter(Boolean);
    const abgleich = abgleicheAblage(liste, dokumente);
    const frei = new Set(abgleich.unverknuepft.map(d => d.id));
    const verwaist = new Set(abgleich.verwaist.map(z => z.dokument_id).filter(Boolean) as string[]);
    const sichtbar = new Map(verknuepfbareDokumente(dokumente).map(d => [d.id, d]));

    const out: SteckbriefAnlage[] = [];
    for (const sb of steckbrief || []) {
        if (!sb || !sb.asplan || sb.status !== 'liegt_vor' || !sb.name) continue;

        const grund: Omit<SteckbriefAnlage, 'stand' | 'ziel' | 'dokumentTitel'> = {
            id: sb.id,
            name: sb.name,
            art: sb.asplan,
            dokumentId: sb.dokument_id || null,
        };

        if (!sb.dokument_id) {
            out.push({ ...grund, dokumentTitel: null, stand: 'ohne_beleg', ziel: null });
            continue;
        }

        const dok = sichtbar.get(sb.dokument_id);
        if (!dok) {
            // Verwaist heisst: eine Zeile zeigt darauf, nur ist das Dokument
            // nicht (mehr) einsehbar — das meldet die Ablagekarte selbst.
            out.push({
                ...grund,
                dokumentTitel: null,
                stand: verwaist.has(sb.dokument_id) ? 'uebernommen' : 'nicht_einsehbar',
                ziel: null,
            });
            continue;
        }

        const titel = dok.titel || dok.dateiname || 'Dokument ohne Titel';
        if (!frei.has(dok.id)) {
            out.push({ ...grund, dokumentTitel: titel, stand: 'uebernommen', ziel: null });
            continue;
        }

        const ziel = liste.find(u => !u.dokument_id && meintDasselbe(u, sb));
        out.push(ziel
            ? { ...grund, dokumentTitel: titel, stand: 'verknuepfen', ziel: { id: ziel.id, bezeichnung: ziel.bezeichnung } }
            : { ...grund, dokumentTitel: titel, stand: 'neu', ziel: null });
    }

    // `sort` ist stabil: innerhalb einer Art bleibt die Reihenfolge der Checkliste.
    return out.sort((a, b) => ART_REIHENFOLGE[a.art] - ART_REIHENFOLGE[b.art]);
};

/**
 * Die neue Zeile zu einem Vorschlag mit Stand `neu` — `null` fuer jeden
 * anderen Stand und dann, wenn inzwischen doch eine Unterlage auf den Beleg
 * zeigt (Doppelklick, zweites Fenster). Geprueft wird gegen den Bestand, der
 * JETZT uebergeben wird, nicht gegen den, aus dem der Vorschlag entstand.
 */
export const entwurfAusSteckbriefAnlage = (
    v: SteckbriefAnlage,
    unterlagen: readonly Unterlage[],
): UnterlagenEntwurf | null => {
    if (!v || v.stand !== 'neu' || !v.dokumentId) return null;
    const liste = (unterlagen || []).filter(Boolean);
    if (liste.some(u => u.dokument_id === v.dokumentId)) return null;

    const katalog = findeDokument(v.id);
    return {
        art: v.art,
        nr: '',
        herausgeber: katalog ? herausgeberVon(katalog) : '',
        bezeichnung: v.name,
        datum: null,
        kennung: '',
        dokument_id: v.dokumentId,
        nachgereicht: false,
        sortierung: naechsteSortierung(liste),
    };
};
